import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/input';
import { createFilterNotification } from '../../api';
import BaseScreen from '../../components/base-screen';
import { ButtonProps } from '../../components/button';
import FilterSelect from '../../components/filter-select';
import { FilterRangeSlider } from '../../components/filter-range-slider';
import useNotificationFilterLimit from '../../hooks/use-notification-filter-limit';
import useNotificationCreateFilter from '../../api/hooks/use-notification-create-filter';
import useNotificationGeneralFilterLimit from '../../hooks/use-notification-general-limin';
import useBoundStore from '../../store';
import { WallMaterial } from '../../lib/dictionary';
import { FilterListProps, NotificationModalProps, squareMetre } from '../../lib/types';
import styles from './styles.module.scss';
import { removeZeroRoomApartments } from '../../lib/utils';
import { ISchemaDataNotificationFilter, notificationFilterSchema } from './validateSchema';
import { useValidatorSchemes, YupErrors } from 'hooks/use-validator-schemes';
import { useEffect, useState } from 'react';

/* ------------------------------------------------------------------------------------ */
interface IFilterPropsNotification extends FilterListProps {
  errors: YupErrors<ISchemaDataNotificationFilter> | null;
}

const NotificationsCreateFilter = (props: IFilterPropsNotification) => {
  const { limits, errors } = props;

  const commissioningYearFrom = useBoundStore((s) => s.commissioningYearFrom);
  const setCommissioningYearFrom = useBoundStore((s) => s.setCommissioningYearFrom);

  const commissioningYearTo = useBoundStore((s) => s.commissioningYearTo);
  const setCommissioningYearTo = useBoundStore((s) => s.setCommissioningYearTo);

  const floorFrom = useBoundStore((s) => s.floorFrom);
  const setFloorFrom = useBoundStore((s) => s.setFloorFrom);

  const floorTo = useBoundStore((s) => s.floorTo);
  const setFloorTo = useBoundStore((s) => s.setFloorTo);

  const floorCountFrom = useBoundStore((s) => s.floorCountFrom);
  const setFloorCountFrom = useBoundStore((s) => s.setFloorCountFrom);

  const floorCountTo = useBoundStore((s) => s.floorCountTo);
  const setFloorCountTo = useBoundStore((s) => s.setFloorCountTo);

  const wallMaterials = useBoundStore((s) => s.wallMaterials);
  const setWallMaterials = useBoundStore((s) => s.setWallMaterials);

  const facilityTypeIds = useBoundStore((s) => s.facilityTypeIds);
  const setFacilityTypeIds = useBoundStore((s) => s.setFacilityTypeIds);

  const bedroomCount = useBoundStore((s) => s.bedroomCount);
  const setBedroomCount = useBoundStore((s) => s.setBedroomCount);

  const priceTo = useBoundStore((s) => s.priceTo);
  const setPriceTo = useBoundStore((s) => s.setPriceTo);

  const priceFrom = useBoundStore((s) => s.priceFrom);
  const setPriceFrom = useBoundStore((s) => s.setPriceFrom);

  const areaTo = useBoundStore((s) => s.areaTo);
  const setAreaTo = useBoundStore((s) => s.setAreaTo);

  const areaFrom = useBoundStore((s) => s.areaFrom);
  const setAreaFrom = useBoundStore((s) => s.setAreaFrom);

  const pricePerSqMTo = useBoundStore((s) => s.pricePerSqMTo);
  const setPricePerSqMTo = useBoundStore((s) => s.setPricePerSqMTo);

  const pricePerSqMFrom = useBoundStore((s) => s.pricePerSqMFrom);
  const setPricePerSqMFrom = useBoundStore((s) => s.setPricePerSqMFrom);

  const transformedFirstlyTypes = !!limits?.facilityTypes?.length
    ? removeZeroRoomApartments(limits?.facilityTypes)?.map((i) => ({
        id: i.id,
        key: i.id,
        fullTitle: i.fullTitle,
      }))
    : [];

  const setShowKeyBoardHeight = useBoundStore((s) => s.setIsShowKeyBoardHeight);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Обязательные:</h2>

      <div className={styles.title} style={{ marginTop: -16 }}>
        <span className='text-sm text-secondary'>
          {`(Укажите значения минимум одного обязательного фильтра)`}
        </span>
      </div>

      <FilterRangeSlider
        isShowSlider={false}
        min={limits?.priceMin}
        max={limits?.priceMax}
        step={1}
        title='Цена'
        valueFrom={priceFrom !== null ? priceFrom : undefined}
        valueTo={priceTo !== null ? priceTo : undefined}
        onChangeTo={(value) => {
          if (value !== null) {
            setPriceTo(Math.round(Number(value)));
          } else {
            setPriceTo(null);
          }
        }}
        onChangeFrom={(value) => {
          if (value !== null) {
            setPriceFrom(Math.round(Number(value)));
          } else {
            setPriceFrom(null);
          }
        }}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        errorFrom={Boolean(errors?.priceFrom)}
        errorTo={Boolean(errors?.priceTo)}
      />

      <FilterRangeSlider
        isShowSlider={false}
        min={limits?.pricePerSqMMin ?? 0}
        max={limits?.pricePerSqMMax ?? 10000000}
        title={`Цена за 1 ${squareMetre}`}
        step={1}
        valueFrom={pricePerSqMFrom !== null ? pricePerSqMFrom : undefined}
        valueTo={pricePerSqMTo !== null ? pricePerSqMTo : undefined}
        onChangeTo={async (value) => {
          if (value !== null) {
            setPricePerSqMTo(Math.round(Number(value)));
          } else {
            setPricePerSqMTo(null);
          }
        }}
        onChangeFrom={(value) => {
          if (value !== null) {
            setPricePerSqMFrom(Math.round(Number(value)));
          } else {
            setPricePerSqMFrom(null);
          }
        }}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        errorFrom={Boolean(errors?.pricePerSqMFrom)}
        errorTo={Boolean(errors?.pricePerSqMTo)}
      />

      <FilterRangeSlider
        isShowSlider={false}
        min={limits?.areaMin}
        max={limits?.areaMax}
        step={1}
        title='Общая площадь'
        valueTo={areaTo !== null ? areaTo : undefined}
        valueFrom={areaFrom !== null ? areaFrom : undefined}
        onChangeTo={(value) => {
          if (value !== null) {
            setAreaTo(Math.round(Number(value)));
          } else {
            setAreaTo(null);
          }
        }}
        onChangeFrom={(value) => {
          if (value !== null) {
            setAreaFrom(Math.round(Number(value)));
          } else {
            setAreaFrom(null);
          }
        }}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        errorFrom={Boolean(errors?.areaFrom)}
        errorTo={Boolean(errors?.areaTo)}
      />

      <h2 className={styles.title}>Не обязательные:</h2>

      <FilterSelect
        label='Материал стен'
        value={wallMaterials}
        onSelect={setWallMaterials}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        options={
          limits?.wallMaterials?.map((item) => ({
            key: item,
            label: WallMaterial.label(item),
          })) || []
        }
      />
      <div className={styles.filterDivider} />

      <FilterSelect
        label='Тип квартиры'
        value={facilityTypeIds}
        onSelect={setFacilityTypeIds}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        options={
          transformedFirstlyTypes?.map((item) => ({
            key: item.id,
            label: item.fullTitle,
          })) || []
        }
      />
      <div className={styles.filterDivider} />

      <FilterSelect
        label='Количество спален'
        value={bedroomCount}
        onSelect={setBedroomCount}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
        options={
          limits?.bedroomCount?.map((item) => ({
            key: item,
            label: item,
          })) || []
        }
      />
      <div className={styles.filterDivider} />

      <FilterRangeSlider
        min={limits?.floorMin}
        max={limits?.floorMax}
        title='Этаж'
        step={1}
        valueFrom={floorFrom !== null ? floorFrom : undefined}
        valueTo={floorTo !== null ? floorTo : undefined}
        onChangeTo={setFloorTo}
        onChangeFrom={setFloorFrom}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
      />
      <div className={styles.filterDivider} />

      <FilterRangeSlider
        min={limits?.floorCountMin}
        max={limits?.floorCountMax}
        title='Количество этажей'
        step={1}
        valueFrom={floorCountFrom !== null ? floorCountFrom : undefined}
        valueTo={floorCountTo !== null ? floorCountTo : undefined}
        onChangeTo={setFloorCountTo}
        onChangeFrom={setFloorCountFrom}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
      />
      <div className={styles.filterDivider} />

      <FilterRangeSlider
        min={limits?.commissioningYearMin}
        max={limits?.commissioningYearMax}
        title='Год сдачи'
        step={1}
        valueFrom={commissioningYearFrom !== null ? commissioningYearFrom : undefined}
        valueTo={commissioningYearTo !== null ? commissioningYearTo : undefined}
        onChangeTo={setCommissioningYearTo}
        onChangeFrom={setCommissioningYearFrom}
        onFocus={() => setShowKeyBoardHeight(true)}
        onBlur={() => setShowKeyBoardHeight(false)}
      />
      <div className={styles.filterDivider} />
    </div>
  );
};

/* ------------------------------------------------------------------------------------ */

export const NotificationModal = (props: NotificationModalProps) => {
  const navigate = useNavigate();
  const createFilterData = useNotificationCreateFilter();
  const { filterLimits } = useNotificationFilterLimit();
  const filterGeneralLimits = useNotificationGeneralFilterLimit();

  const generaLimits = {
    ...filterLimits,
    ...filterGeneralLimits,
    apartmentComplexes: filterGeneralLimits?.apartmentComplexes,
    developers: filterGeneralLimits?.developers,
    facilityTypes: filterGeneralLimits?.facilityTypes,
    renovationTypes: filterGeneralLimits?.renovationTypes,
    sellerTypes: filterGeneralLimits?.sellerTypes,
    wallMaterials: filterGeneralLimits?.wallMaterials,

    floorMin: 1,
    floorMax: 100,
    floorCountMin: 1,
    floorCountMax: 100,
    commissioningYearMin: new Date().getFullYear() - 1,
    commissioningYearMax: new Date().getFullYear() + 20,
  };

  const title = useBoundStore((s) => s.title);
  const setTitle = useBoundStore((s) => s.setTitle);
  const clearFilterForm = useBoundStore((s) => s.clearFilterForm);
  const [isLoading, setIsLoading] = useState(false);

  const {
    errors,
    validate,
    reset: resetErrors,
  } = useValidatorSchemes<ISchemaDataNotificationFilter>(notificationFilterSchema);

  useEffect(() => {
    const resetAll = () => {
      clearFilterForm();
      resetErrors();
    };
    return () => resetAll();
  }, []);

  const priceTo = useBoundStore((s) => s.priceTo);
  const priceFrom = useBoundStore((s) => s.priceFrom);
  const areaTo = useBoundStore((s) => s.areaTo);
  const areaFrom = useBoundStore((s) => s.areaFrom);
  const pricePerSqMTo = useBoundStore((s) => s.pricePerSqMTo);
  const pricePerSqMFrom = useBoundStore((s) => s.pricePerSqMFrom);

  const onValidateForm = async () => {
    return await validate({
      priceTo: priceTo,
      priceFrom: priceFrom,
      areaTo: areaTo,
      areaFrom: areaFrom,
      pricePerSqMFrom: pricePerSqMFrom,
      pricePerSqMTo: pricePerSqMTo,
      title: title as string,
    });
  };

  useEffect(() => {
    onChangeFormItem();
  }, [priceTo, priceFrom, areaTo, areaFrom, pricePerSqMTo, pricePerSqMFrom, title]);

  const onChangeFormItem = async () => {
    if (errors) {
      await onValidateForm();
    }
  };

  const buttons: ButtonProps[] = [
    {
      variant: 'gray',
      label: 'Очистить',
      onClick: clearFilterForm,
    },
    {
      loading: isLoading,
      label: 'Создать фильтр',
      onClick: async () => {
        try {
          const isValid = await onValidateForm();
          if (isValid) {
            setIsLoading(true);
            try {
              await createFilterNotification(createFilterData);
              clearFilterForm();
              navigate('/notification', { state: { defaultTab: 1 } });
              setIsLoading(false);
            } catch (error) {
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.log(error);
        }
      },
    },
  ];

  const transformErrors = () => {
    if (errors) {
      return Object.keys(errors).map((error) => {
        return errors[error as keyof typeof errors][0];
      });
    }
    return undefined;
  };

  const keyBoardHeight = useBoundStore((s) => s.keyBoardHeight);
  const isShowKeyBoardHeight = useBoundStore((s) => s.isShowKeyBoardHeight);
  const setIsShowKeyBoardHeight = useBoundStore((s) => s.setIsShowKeyBoardHeight);

  return (
    <BaseScreen
      title='Создание фильтра'
      buttons={buttons}
      footerClassName={styles.footer}
      floatingButtons={true}
      floatingButtonsAppear={isShowKeyBoardHeight}
      error={transformErrors()?.[0]}
    >
      <div className={clsx('grid-1', styles.mainFields)}>
        <Input
          placeholder={'Не более 40 символов'}
          name='title'
          label='Название уведомления'
          value={title}
          onChange={(value) => {
            setTitle(value);
          }}
          onFocus={() => setIsShowKeyBoardHeight(true)}
          onBlur={() => setIsShowKeyBoardHeight(false)}
          isMainPlaceholder
          error={Boolean(errors?.title)}
        />
      </div>
      <NotificationsCreateFilter limits={generaLimits} errors={errors} />
      {!!isShowKeyBoardHeight && (
        <div className={styles.stretchableBlock} style={{ height: Number(keyBoardHeight - 15) }} />
      )}
    </BaseScreen>
  );
};
export default NotificationModal;
